import { useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import { Card, Icon, Skeleton } from "@xinghai/ui";
import { qualityApi } from "../../lib/api";
import { qk } from "../../lib/queries";
import { useAuth } from "../auth/useAuth";
import { readTally } from "./draft";

interface Bar {
  date: string;
  label: string;
  count: number;
}

/** Last 7 calendar days, oldest first. */
function buildBars(
  tally: Record<string, number>,
  series?: Array<{ date: string; count: number }>,
): Bar[] {
  const fromServer = new Map((series ?? []).map((s) => [s.date, s.count]));
  const out: Bar[] = [];
  const today = new Date();
  for (let i = 6; i >= 0; i -= 1) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const key = d.toISOString().slice(0, 10);
    out.push({
      date: key,
      label: `${d.getMonth() + 1}/${d.getDate()}`,
      count: fromServer.get(key) ?? tally[key] ?? 0,
    });
  }
  return out;
}

function pct(v: number | null | undefined): string {
  return v === null || v === undefined ? "—" : `${Math.round(v * 100)}%`;
}

/** docs/UX.md 8 — 我的进度. Only ever the signed-in expert's own row. */
export function MyProgressPage() {
  const { me } = useAuth();
  const userId = me?.user.id ?? "anon";

  const q = useQuery({
    queryKey: qk.qualityAnnotators,
    queryFn: () => qualityApi.annotators(),
  });
  // docs/ROADMAP.md B5 —— 本周配对一致性 + 「本周未达标，建议先做校准批」
  const weekly = useQuery({
    queryKey: qk.qualityWeekly(8),
    queryFn: ({ signal }) => qualityApi.weekly({ weeks: 8 }, signal),
    retry: false,
  });
  const iaa = weekly.data?.my_status ?? null;
  const myWeeks = useMemo(
    () =>
      (weekly.data?.weeks ?? [])
        .map((w) => ({ week: w.week, ...(w.annotators[0] ?? null) }))
        .filter((w): w is typeof w & { kappa: number | null } => "kappa" in w)
        .slice(0, 6),
    [weekly.data],
  );

  const row = useMemo(() => {
    const rows = q.data?.annotators ?? [];
    return rows.find((r) => r.annotator_id === userId) ?? rows[0] ?? null;
  }, [q.data, userId]);

  const bars = useMemo(() => buildBars(readTally(userId), row?.last_7_days), [userId, row]);
  const maxCount = Math.max(1, ...bars.map((b) => b.count));

  if (q.isPending) {
    return (
      <div className="xz-container xz-stack" aria-busy="true">
        <Skeleton variant="rect" height={120} />
        <Skeleton variant="rect" height={180} />
      </div>
    );
  }

  return (
    <div className="xz-container xz-stack xz-progress">
      <h1 className="gz-type-heading">我的进度</h1>

      {iaa?.hint && (
        <p className={iaa.withheld ? "gz-notice gz-notice--error" : "gz-notice"} role="status">
          <Icon id="ui-warning" size={20} />
          <span>
            {iaa.hint}
            {iaa.kappa !== null && (
              <>
                （本周 κ {iaa.kappa.toFixed(2)}，目标 ≥ {iaa.gate.toFixed(2)}）
              </>
            )}
            {iaa.withheld && "。在校准批达标之前，不会再派新的普通条目。"}
          </span>
        </p>
      )}

      <Card padding="md">
        <dl className="xz-progress__counts">
          <div>
            <dt>今日已标</dt>
            <dd className="gz-type-score">{row?.today ?? 0}</dd>
          </div>
          <div>
            <dt>累计</dt>
            <dd className="gz-type-score">{row?.total ?? 0}</dd>
          </div>
          <div>
            <dt>已合并</dt>
            <dd className="gz-type-score">{row?.merged_with_peers ?? 0}</dd>
          </div>
        </dl>
      </Card>

      <Card padding="md" title="一致性">
        <dl className="xz-progress__counts">
          <div>
            <dt>一致率</dt>
            <dd className="gz-type-score">{pct(row?.agreement_rate)}</dd>
          </div>
          <div>
            <dt>κ</dt>
            <dd className="gz-type-score">
              {row ? row.kappa.toFixed(2) : "—"}
              {row && (
                <span className="xz-progress__gate">
                  <Icon id={row.kappa_pass ? "ui-check-circle" : "ui-warning"} size={16} />
                  {row.kappa_pass ? "达标" : "未达标"}
                </span>
              )}
            </dd>
          </div>
          {row?.golden_hit_rate != null && (
            <div>
              <dt>黄金题命中率</dt>
              <dd className="gz-type-score">
                {pct(row.golden_hit_rate)}
                {row.golden_pass !== null && (
                  <span className="xz-progress__gate">
                    <Icon id={row.golden_pass ? "ui-check-circle" : "ui-warning"} size={16} />
                    {row.golden_pass ? "达标" : "未达标"}
                  </span>
                )}
              </dd>
            </div>
          )}
        </dl>
        <p className="gz-caption">一致率与 κ 只反映你自己的数据，不显示另一位专家的身份或评分。</p>
      </Card>

      <Card padding="md" title="近 7 天">
        <ul className="xz-progress__bars" aria-label="近 7 天标注量">
          {bars.map((b) => (
            <li key={b.date} className="xz-progress__bar">
              <span className="xz-progress__barcount">{b.count}</span>
              <span
                className="xz-progress__barfill"
                style={{ height: `${Math.round((b.count / maxCount) * 100)}%` }}
                aria-hidden="true"
              />
              <span className="gz-caption">{b.label}</span>
            </li>
          ))}
        </ul>
        {!row?.last_7_days && (
          <p className="gz-caption">近 7 天曲线来自本机记录，换设备后会重新开始累计。</p>
        )}
      </Card>

      {myWeeks.length > 0 && (
        <Card padding="md" title="按周的一致性">
          <ul className="xz-progress__weeks" aria-label="按周的配对一致性">
            {myWeeks.map((w) => (
              <li key={w.week} className="xz-progress__week">
                <span className="gz-caption">{w.week}</span>
                <span className="gz-type-data">
                  κ {w.kappa === null ? "—" : w.kappa.toFixed(2)}
                </span>
                <span className="xz-progress__gate">
                  <Icon id={w.pass === false ? "ui-warning" : "ui-check-circle"} size={16} />
                  {w.pass === false ? "未达标" : w.pass === true ? "达标" : "数据不足"}
                </span>
              </li>
            ))}
          </ul>
          <p className="gz-caption">
            按 ISO 周（周一至周日，北京时间）统计你与另一位专家在同一条录音上的一致性；
            对方的身份始终不显示。目标 κ ≥ {(iaa?.gate ?? 0.8).toFixed(2)}。
          </p>
        </Card>
      )}
    </div>
  );
}
